import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';
import ComplimentaryModal from '../components/ComplimentaryModal';

const SWIM_RATES = [
  { type:'adult_resident',    label:'Adult Resident',       price:500  },
  { type:'child_resident',    label:'Child Resident (<12)', price:300  },
  { type:'adult_nonresident', label:'Adult Non-Resident',   price:1000 },
  { type:'child_nonresident', label:'Child Non-Resident',   price:600  },
  { type:'hotel_guest',       label:'Hotel Guest (FOC)',    price:0    },
];

const PAY_MODES = [
  { value:'cash',          label:'Cash',          color:'#2ecc71' },
  { value:'mpesa',         label:'M-Pesa',        color:'#00b300' },
  { value:'card',          label:'Card / PDQ',    color:'#9b59b6' },
  { value:'room_charge',   label:'Room Charge',   color:'#e67e22' },
  { value:'complimentary', label:'Complimentary', color:'#c8a96e' },
];

export default function SwimmingAttendantPage({ notify }) {
  const [tab, setTab]       = useState('entry');
  const [entries, setEntries] = useState([]);
  const [guests, setGuests]   = useState([]);
  const [showComp, setShowComp] = useState(false);

  const blank = { visitor_name:'', visitor_type:'adult_resident', adults:1, children:0, payment_mode:'cash', tx_code:'', guest_id:'' };
  const [form, setForm]   = useState(blank);
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, g] = await Promise.all([api.getPoolEntries(), api.getGuests()]);
      setEntries(e.data.results || e.data);
      setGuests((g.data.results || g.data).filter(x => x.status === 'checked-in'));
    } catch {}
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const selRate = SWIM_RATES.find(r => r.type === form.visitor_type) || SWIM_RATES[0];
  const baseAmt = selRate.price * (form.adults || 1);
  const childAmt = form.children > 0 ? (SWIM_RATES.find(r => r.type.includes('child') && r.type.includes(form.visitor_type.includes('nonresident') ? 'nonresident' : 'resident'))?.price || 0) * form.children : 0;
  const totalAmt = baseAmt + childAmt;

  const submit = async () => {
    if (!form.visitor_name && !form.guest_id) { notify('Visitor name required', 'error'); return; }
    if (form.payment_mode === 'complimentary') { setShowComp(true); return; }
    if (totalAmt > 0 && form.payment_mode !== 'room_charge' && !form.tx_code) { notify('Transaction code required for paid entries', 'error'); return; }
    setBusy(true);
    try {
      await api.createPoolEntry({
        ...form,
        adults: parseInt(form.adults) || 1,
        children: parseInt(form.children) || 0,
        amount: totalAmt,
      });
      notify('Pool entry recorded ✅');
      setForm(blank);
      load();
    } catch(e) { notify(e.response?.data?.detail || 'Error recording entry', 'error'); }
    finally { setBusy(false); }
  };

  const todayEntries = entries.filter(e => {
    const d = new Date(e.created_at || e.entry_date);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  });
  const todayRevenue = todayEntries.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const currentGuests = todayEntries.filter(e => !e.exit_time).length;

  return (
    <div>
      <PageHeader icon="🏊" title="Pool Attendant" sub="Record swimmer entries and monitor pool activity" />

      <StatCards stats={[
        { label:'Guests in Pool', value: currentGuests, color:'#0ea5e9', icon:'🏊' },
        { label:'Today\'s Entries', value: todayEntries.length, color:'#2ecc71', icon:'👥' },
        { label:'Today\'s Revenue', value: fmtKES(todayRevenue), color:'#f39c12', icon:'💰' },
      ]} />

      <TabBar
        tabs={[
          { id:'entry', label:'New Entry', icon:'➕' },
          { id:'active', label:`Active Guests (${currentGuests})`, icon:'🏊' },
          { id:'history', label:'Today\'s Log', icon:'📋' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'entry' && (
        <Card title="Record Pool Entry">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:12 }}>
            <Field label="Visitor Type">
              <select style={inputStyle} value={form.visitor_type} onChange={e => setForm(p => ({...p, visitor_type:e.target.value}))}>
                {SWIM_RATES.map(r => (
                  <option key={r.type} value={r.type}>{r.label} — {r.price > 0 ? fmtKES(r.price) : 'Free'}</option>
                ))}
              </select>
            </Field>

            {form.visitor_type === 'hotel_guest' ? (
              <Field label="Select Guest">
                <select style={inputStyle} value={form.guest_id} onChange={e => {
                  const g = guests.find(x => String(x.id) === e.target.value);
                  setForm(p => ({...p, guest_id:e.target.value, visitor_name: g ? g.guest_name : ''}));
                }}>
                  <option value="">— Select checked-in guest —</option>
                  {guests.map(g => <option key={g.id} value={g.id}>{g.guest_name} — Room {g.room_number}</option>)}
                </select>
              </Field>
            ) : (
              <Field label="Visitor Name">
                <input style={inputStyle} value={form.visitor_name} onChange={e => setForm(p => ({...p, visitor_name:e.target.value}))} placeholder="Full name" />
              </Field>
            )}

            <Field label="Number of Adults">
              <input type="number" style={inputStyle} min={1} value={form.adults} onChange={e => setForm(p => ({...p, adults:e.target.value}))} />
            </Field>

            <Field label="Number of Children (under 12)">
              <input type="number" style={inputStyle} min={0} value={form.children} onChange={e => setForm(p => ({...p, children:e.target.value}))} />
            </Field>

            {totalAmt > 0 && (
              <Field label="Payment Mode">
                <select style={inputStyle} value={form.payment_mode} onChange={e => setForm(p => ({...p, payment_mode:e.target.value}))}>
                  {PAY_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
            )}

            {totalAmt > 0 && form.payment_mode !== 'room_charge' && (
              <Field label={form.payment_mode === 'mpesa' ? 'M-Pesa Code' : 'Reference / Receipt'}>
                <input style={inputStyle} value={form.tx_code} onChange={e => setForm(p => ({...p, tx_code:e.target.value}))} placeholder="Transaction code" />
              </Field>
            )}
          </div>

          <div style={{ marginTop:14, padding:12, background:'var(--bgInput)', borderRadius:6, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:13, color:'var(--textMuted)', marginBottom:4 }}>Entry Summary</div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ color:'var(--textSecond)', fontSize:13 }}>
                {form.adults} adult{form.adults != 1 ? 's' : ''}{form.children > 0 ? ` + ${form.children} child${form.children != 1 ? 'ren' : ''}` : ''} — {SWIM_RATES.find(r=>r.type===form.visitor_type)?.label}
              </span>
              <span style={{ fontSize:20, fontWeight:800, color: totalAmt > 0 ? '#2ecc71' : '#3498db' }}>
                {totalAmt > 0 ? fmtKES(totalAmt) : 'FREE'}
              </span>
            </div>
          </div>

          <button style={{...btnPrimary, marginTop:14, background:'#0ea5e9'}} onClick={submit} disabled={busy}>
            {busy ? '⏳ Recording…' : '✅ Record Entry'}
          </button>
        </Card>
      )}

      {tab === 'active' && (
        <Card title="Currently in Pool">
          {currentGuests === 0 ? <Empty text="No guests currently in the pool" /> : (
            <div style={{ display:'grid', gap:8 }}>
              {todayEntries.filter(e => !e.exit_time).map((e, i) => (
                <div key={i} style={{ padding:'10px 14px', background:'var(--bgInput)', borderRadius:6, border:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:700, color:'var(--textPrimary)' }}>{e.visitor_name || `Guest #${e.id}`}</div>
                    <div style={{ fontSize:11, color:'var(--textMuted)', marginTop:2 }}>
                      {e.visitor_type_display || e.visitor_type} · Entered {new Date(e.created_at || e.entry_date).toLocaleTimeString('en-KE', {hour:'2-digit', minute:'2-digit'})}
                    </div>
                  </div>
                  <span style={{ padding:'3px 10px', borderRadius:3, background:'#0ea5e9', color:'#fff', fontSize:10, fontWeight:700 }}>IN POOL</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'history' && (
        <Card title="Today's Entry Log">
          {todayEntries.length === 0 ? <Empty text="No entries recorded today" /> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>
                    {['Time','Name','Type','Adults','Children','Amount','Payment','Status'].map(h => (
                      <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:10, color:'var(--textMuted)', letterSpacing:1, textTransform:'uppercase', borderBottom:'2px solid var(--border)', background:'var(--bgInput)', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {todayEntries.map((e, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'8px 10px', color:'var(--textMuted)', fontSize:11 }}>{new Date(e.created_at || e.entry_date).toLocaleTimeString('en-KE', {hour:'2-digit', minute:'2-digit'})}</td>
                      <td style={{ padding:'8px 10px', fontWeight:600, color:'var(--textPrimary)' }}>{e.visitor_name || '—'}</td>
                      <td style={{ padding:'8px 10px', fontSize:11, color:'var(--textSecond)' }}>{e.visitor_type_display || e.visitor_type}</td>
                      <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--textSecond)' }}>{e.adults || 1}</td>
                      <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--textSecond)' }}>{e.children || 0}</td>
                      <td style={{ padding:'8px 10px', fontWeight:700, color: parseFloat(e.amount) > 0 ? '#2ecc71' : '#3498db' }}>{parseFloat(e.amount) > 0 ? fmtKES(e.amount) : 'FOC'}</td>
                      <td style={{ padding:'8px 10px', fontSize:11 }}><span style={{ padding:'2px 7px', borderRadius:3, background: e.payment_mode === 'mpesa' ? '#00b300' : e.payment_mode === 'cash' ? '#2ecc71' : '#9b59b6', color:'#fff', fontWeight:700 }}>{e.payment_mode || '—'}</span></td>
                      <td style={{ padding:'8px 10px' }}><span style={{ padding:'2px 7px', borderRadius:3, background: e.exit_time ? '#7f8c8d' : '#0ea5e9', color:'#fff', fontSize:10, fontWeight:700 }}>{e.exit_time ? 'EXITED' : 'ACTIVE'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      {showComp && (
        <ComplimentaryModal
          amount={totalAmt}
          guestName={form.visitor_name}
          notify={notify}
          onClose={() => setShowComp(false)}
          onSubmitted={() => { setForm(blank); load(); }}
        />
      )}
    </div>
  );
}
